/**
 * The user-visible contract of the inline geography-taxonomy switch: the
 * trigger's `data-testid` and the three messages the switch narrates.
 *
 * Hoisted out of the app (next to `offer-marker`, for the same reason) so the
 * drawer, the turn-loop mirror view and the Playwright suite key off ONE
 * string each. The Playwright suite cannot import the app module that owns the
 * behaviour — its `applyGeographyTaxonomyPreference` dependency pulls the
 * `@revelio/core` barrel, which reads `window` at module load and kills spec
 * collection — so a leaf module with NO imports is what makes the constants
 * shareable at all. Keep it that way.
 */

/** The inline `[phrase](offer:geo_tax)` narration trigger. */
export const GEO_TAX_OFFER_TRIGGER_TESTID = 'offer-trigger-geo-tax';

export const GEO_TAX_SWITCH_REAPPLYING_MESSAGE =
  "Switched your geography view — re-running that search so it's filtered the same way under the new one.";

export const GEO_TAX_SWITCH_NO_REAPPLY_MESSAGE =
  'Switched your geography view. Open a page with filters and ask again to see it applied there.';

/**
 * Off a filterable page (the assistant homepage, a report), the switch has
 * nothing to re-key in place, so it re-asks the question that earned the
 * offer under the new view instead — the answer then lands the page link with
 * the geography the user actually wanted.
 */
export const GEO_TAX_SWITCH_REASKING_MESSAGE =
  'Switched your geography view — asking that again under the new one.';

/**
 * The offer stays clickable in the transcript after it has been taken (two
 * people clicked one 2026-09-03 and got two re-asks). Once the account is
 * already on the offered view there is nothing to switch, so the click is a
 * no-op with this note rather than another preference write and re-ask.
 */
export const GEO_TAX_SWITCH_ALREADY_ON_MESSAGE =
  "You're already on that geography view — ask again to see it applied.";

export const GEO_TAX_SWITCH_REAPPLY_FAILED_MESSAGE =
  "Switched your geography view, but I couldn't re-resolve the filters for it — try asking again.";

/**
 * Model-actionable note for a geography taxonomy mismatch, or `null` when
 * there is none (the overwhelmingly common case). Read defensively: the field
 * is optional and older/cached responses predate it.
 *
 * Shared because it has to be emitted TWICE in one turn. `search_filters` runs
 * SERVER-side, so its result — where this note originates — lives only in that
 * step's model messages and never reaches the FE. The tool that consumes the
 * artifact (`apply_filters`, `navigate`, `offer_page_or_custom`, …) is a
 * CLIENT tool, which ends the step; by the next step, where the model actually
 * writes the user-facing narration, the note is gone and rule 9c has nothing
 * to fire on. So the FE re-emits it from the same artifact on EVERY
 * artifact-consuming client tool's result (the client-tool registry does it
 * once, centrally). One builder, so the two can never drift into saying
 * different things.
 */
export function buildGeoTaxonomyMismatchLine(mismatch: unknown): string | null {
  if (typeof mismatch !== 'object' || mismatch === null) return null;
  const items = (mismatch as { items?: unknown }).items;
  if (!Array.isArray(items)) return null;
  const asked = items
    .map((item) => {
      if (typeof item !== 'object' || item === null) return null;
      const {
        asked: askedName,
        matchedName,
        level,
      } = item as {
        asked?: unknown;
        matchedName?: unknown;
        level?: unknown;
      };
      if (typeof askedName !== 'string' || askedName.length === 0) return null;
      if (typeof matchedName !== 'string' || matchedName.length === 0) {
        return null;
      }
      const levelLabel = typeof level === 'string' ? level : 'location';
      const applied = describeAppliedAs(
        (item as { appliedAs?: unknown }).appliedAs
      );
      return `"${askedName}" (exists as ${matchedName}, a ${levelLabel}${applied ? `; resolved here as ${applied}` : ''})`;
    })
    .filter((entry): entry is string => entry !== null)
    .join('; ');
  if (!asked) return null;

  const label = (mismatch as { otherTaxonomyLabel?: unknown })
    .otherTaxonomyLabel;
  const taxonomyName =
    typeof label === 'string' && label.length > 0 ? label : 'the other';

  return (
    `GEO TAXONOMY NOTE: ${asked} — that level only exists in the user's OTHER ` +
    `geography view, "${taxonomyName}". The resolved filter is the ` +
    `closest fit in their current one. Disclose the substitution in one clause ` +
    `— name what they asked for AND what was resolved instead (its level and, ` +
    `where listed, the places), never just "the closest fit" — ` +
    `and end the disclosure clause on a ` +
    `[switch to ${taxonomyName} geography](offer:geo_tax) marker (rule 9c). ` +
    `Use exactly that view name — never a raw level tag like "city_core".`
  );
}

/** Names listed before the rest collapse into "and N more". */
const APPLIED_AS_NAMES_SHOWN = 4;

/**
 * "its 10 Metro Areas: Houston, Dallas, Austin, Fort Worth and 6 more" — the
 * substitute as it was actually applied, so the disclosure can name it.
 * `null` when the payload carries no usable list (older responses, or an ask
 * that resolved to nothing in the user's own taxonomy).
 */
function describeAppliedAs(appliedAs: unknown): string | null {
  if (!Array.isArray(appliedAs)) return null;
  const entries = appliedAs.flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) return [];
    const { name, level } = entry as { name?: unknown; level?: unknown };
    if (typeof name !== 'string' || name.length === 0) return [];
    return [{ name, level: typeof level === 'string' ? level : 'location' }];
  });
  const first = entries[0];
  if (!first) return null;
  const shown = entries.slice(0, APPLIED_AS_NAMES_SHOWN);
  const rest = entries.length - shown.length;
  const sharedLevel = entries.every((entry) => entry.level === first.level)
    ? first.level
    : null;
  const names = shown.map((entry) =>
    sharedLevel ? entry.name : `${entry.name} (${entry.level})`
  );
  let list: string;
  if (rest > 0) {
    list = `${names.join(', ')} and ${rest} more`;
  } else if (names.length > 1) {
    list = `${names.slice(0, -1).join(', ')} and ${names.slice(-1).join('')}`;
  } else {
    list = names.join('');
  }
  if (sharedLevel === null) return list;
  return entries.length === 1
    ? `the ${sharedLevel} ${list}`
    : `its ${entries.length} ${sharedLevel}s: ${list}`;
}

const BREAKDOWN_LEVEL_NOUNS = {
  region: 'regions',
  country: 'countries',
  state: 'states',
  metro: 'metro areas',
  county: 'counties',
  city: 'cities',
} as const;

/**
 * The note a chart result carries when the breakdown level asked for is not a
 * rung of the user's geography view (states on Metro Area & City, metros on
 * State & County): the chart draws the view's own places instead, and the
 * user is told so and offered the switch, the same way as a place name that
 * only exists in the other view (rule 9c).
 */
export function buildGeoBreakdownLevelNoteLine(params: {
  askedLevel: keyof typeof BREAKDOWN_LEVEL_NOUNS;
  currentTaxonomyLabel: string;
  otherTaxonomyLabel: string;
}): string {
  const asked = BREAKDOWN_LEVEL_NOUNS[params.askedLevel];
  return (
    `GEO TAXONOMY NOTE: the user asked for a breakdown by ${asked}, a level their ` +
    `current geography view, "${params.currentTaxonomyLabel}", does not have, so ` +
    `the chart breaks the place down by that view's own levels instead. Disclose ` +
    `that in one clause, naming the level asked for and the places the chart ` +
    `actually shows, and end it on a ` +
    `[switch to ${params.otherTaxonomyLabel} geography](offer:geo_tax) marker (rule 9c). ` +
    `Never call the places on the chart ${asked}.`
  );
}

/**
 * The same note on a read, which names the level it read instead because its
 * rows reach the model as text rather than as a drawn chart.
 */
export function buildReadGeoBreakdownLevelNoteLine(params: {
  askedLevel: keyof typeof BREAKDOWN_LEVEL_NOUNS;
  readLevel: keyof typeof BREAKDOWN_LEVEL_NOUNS;
  currentTaxonomyLabel: string;
  otherTaxonomyLabel: string;
}): string {
  const asked = BREAKDOWN_LEVEL_NOUNS[params.askedLevel];
  const read = BREAKDOWN_LEVEL_NOUNS[params.readLevel];
  return (
    `GEO TAXONOMY NOTE: the user asked for a breakdown by ${asked}, a level their ` +
    `current geography view, "${params.currentTaxonomyLabel}", does not have, so ` +
    `the geography rows below are ${read} instead. Disclose that in one clause, ` +
    `naming the level asked for and the level read, and end it on a ` +
    `[switch to ${params.otherTaxonomyLabel} geography](offer:geo_tax) marker (rule 9c). ` +
    `Never call these rows ${asked}.`
  );
}
